import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { DisabilityKind, DisabilityType } from '../../../api/app-types/disability-type.app';
import { DISABILITY_TYPE_SERVICE } from '../../../api/services/disability-type.api';
import { LOGGER } from '../../logging/log.service';
import { PaginatedResponse } from '../../pagination/paginated-response';
import { ApiResponse, Metadata } from '../../../api/api-types/api-response.api';
import { NeighborContext } from '../../../api/app-types/neighbors.app';

/**
 * The resolver is used to fetch the disability type details before the route is activated.
 * It ensures that the data is available when the component is initialized.
 * @param route The activated route snapshot.
 * @param _state The router state snapshot.
 * @returns The disability type details.
 */
export const disabilityKindResolver: ResolveFn<ApiResponse<DisabilityKind, Metadata>> = (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const logger = inject(LOGGER);
	const api = inject(DISABILITY_TYPE_SERVICE);
	const clientId = route.paramMap.get('id');
	const typeId = route.paramMap.get('disabilityTypeId');
	logger.log(`Fetching disability type with ID: ${typeId}`);
	return api.getSingleKind(Number(clientId), Number(typeId));
};

/**
 * The resolver is used to fetch all disability types for a specific client.
 * @param route The activated route snapshot.
 * @param _state The router state snapshot.
 * @returns An array of disability types.
 */
export const disabilityTypesResolver: ResolveFn<PaginatedResponse<DisabilityType>> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(DISABILITY_TYPE_SERVICE);
	const env = inject(ENVIRONMENT);
	const response = await api.getAll(env.pagination.offset, env.pagination.range); // Adjust offset and range as needed
	return response;
};

export const disabilityKindsResolver: ResolveFn<PaginatedResponse<DisabilityKind>> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const logger = inject(LOGGER);
	const api = inject(DISABILITY_TYPE_SERVICE);
	const env = inject(ENVIRONMENT);
	const clientId = route.paramMap.get('id');
	logger.log(`Fetching all disability kinds for client ID: ${clientId}`);
	const response = await api.getAllKinds(Number.parseInt(clientId!), env.pagination.offset, env.pagination.range);
	return response;
};

export const disabilityKindNeighborsResolver: ResolveFn<NeighborContext<DisabilityKind>> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(DISABILITY_TYPE_SERVICE);
	const clientId = route.paramMap.get('id')!;
	const kindId = route.paramMap.get('disabilityTypeId')!;
	return api.getKindNeighbors(clientId, kindId);
};
